import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import { useTranslation } from 'react-i18next';
import { Calendar, MoreIcon, PlayerPause, PlayerPlay, PlayerStop } from '../../../assets';
import { useOutsideClick } from '../../../hooks/useOutsideClick.hook';
import SpeakerModal from './modal.component';
import { Speaker } from '../speakers.page';
import {
  formatPlaybackTime,
  getPositionTagStyle,
  getSpeakerAvatarColor,
  getSpeakerInitials,
  getWaveBarHeights,
} from '../utils/speaker-ui';
import { apiService } from '../../../services/api.service';
import { toast } from 'react-toastify';

export type PlaybackState = 'idle' | 'playing' | 'paused';

interface IProps {
  item: Speaker;
  index: number;
  onRefresh: () => void;
  activeId: string | null;
  onActivate: (id: string) => void;
  onDeactivate: () => void;
}

const cn = createCn('speakers', classes);

const SpeakerCard: React.FC<IProps> = ({
  item,
  index,
  onRefresh,
  activeId,
  onActivate,
  onDeactivate,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [playback, setPlayback] = useState<PlaybackState>('idle');
  const [currentTime, setCurrentTime] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useOutsideClick(dropdownRef, () => setMenuOpen(false));

  const initials = getSpeakerInitials(item.name);
  const avatarColor = getSpeakerAvatarColor(index);
  const barHeights = useMemo(() => getWaveBarHeights(item.id), [item.id]);
  const tagStyle = getPositionTagStyle(item.position);
  const audioUrl = item.hasVoice ? apiService.getSpeakerAudioUrl(item.id) : null;
  const isActive = activeId === item.id;
  const isPlaying = playback === 'playing';
  const isPaused = playback === 'paused';
  const accentColor = isPlaying ? '#F59E0B' : '#22C55E';
  const progressPct = durationSec > 0 ? Math.min(100, (currentTime / durationSec) * 100) : 0;

  const stopLocal = (resetTime = true) => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      if (resetTime) audio.currentTime = 0;
    }
    setPlayback('idle');
    setCurrentTime(0);
    if (isActive) onDeactivate();
  };

  useEffect(() => {
    if (!audioUrl) return;
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const onMeta = () => {
      if (Number.isFinite(audio.duration)) {
        setDurationSec(audio.duration);
      }
    };
    const onTime = () => setCurrentTime(audio.currentTime);
    const onEnded = () => stopLocal(true);

    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    audio.load();

    return () => {
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
      audio.pause();
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  useEffect(() => {
    if (activeId !== null && activeId !== item.id && playback !== 'idle') {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      setPlayback('idle');
      setCurrentTime(0);
    }
  }, [activeId, item.id, playback]);

  const handlePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setPlayback('paused');
      return;
    }

    onActivate(item.id);
    audio.play()
      .then(() => setPlayback('playing'))
      .catch(() => toast.error(t('speakers.play-error')));
  };

  const handlePauseOrStop = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      audio.currentTime = 0;
      setPlayback('idle');
      setCurrentTime(0);
      onDeactivate();
      return;
    }

    if (isPaused) {
      audio.pause();
      setPlayback('paused');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('speakers.delete-confirm', { name: item.name }))) return;
    stopLocal(true);
    setMenuOpen(false);
    try {
      await apiService.deleteSpeaker(item.id);
      onRefresh();
      toast.success(t('speakers.deleted'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('speakers.delete-error'));
    }
  };

  return (
    <>
      <article
        className={cn('card', { playing: isPlaying })}
        style={isPlaying ? { borderColor: '#22C55E' } : undefined}
      >
        <div className={cn('card-top')}>
          <div className={cn('card-identity')}>
            <div className={cn('avatar')} style={{ background: `${avatarColor}22`, color: avatarColor }}>
              {initials}
            </div>
            <div className={cn('card-text')}>
              <div className={cn('card-name')}>{item.name}</div>
            </div>
          </div>
          <div className={cn('card-menu')} ref={dropdownRef}>
            <button
              type="button"
              className={cn('menu-btn')}
              aria-label={t('speakers.edit')}
              onClick={() => setMenuOpen(prev => !prev)}
            >
              <MoreIcon />
            </button>
            {menuOpen && (
              <div className={cn('dropdown')}>
                <div
                  className={cn('dropdown-item')}
                  onClick={() => {
                    setOpenModal(true);
                    setMenuOpen(false);
                  }}
                >
                  {t('speakers.edit')}
                </div>
                <div className={cn('dropdown-item', { danger: true })} onClick={handleDelete}>
                  {t('action.delete')}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={cn('wave-player', { disabled: !item.hasVoice })}>
          <div className={cn('player-controls')}>
            <button
              type="button"
              className={cn('play-btn', {
                active: isPlaying,
                neutral: isPaused,
              })}
              style={isPlaying ? { background: accentColor } : isPaused ? undefined : { background: '#22C55E' }}
              aria-label="Play"
              onClick={handlePlay}
              disabled={!item.hasVoice}
            >
              <PlayerPlay />
            </button>
            <button
              type="button"
              className={cn('stop-btn', { active: isPlaying })}
              aria-label={isPlaying ? 'Стоп' : 'Пауза'}
              onClick={handlePauseOrStop}
              disabled={!item.hasVoice || playback === 'idle'}
            >
              {isPlaying ? <PlayerStop /> : <PlayerPause />}
            </button>
          </div>
          <div className={cn('wave-bars')}>
            {barHeights.map((height, i) => (
              <span
                key={i}
                className={cn('wave-bar')}
                style={{
                  height: `${height}px`,
                  background: isPlaying ? 'var(--color-gold-bg)' : 'var(--color-primary-muted-bg)',
                }}
              />
            ))}
          </div>
        </div>

        <div className={cn('progress-row')}>
          <span className={cn('time-cur')}>{formatPlaybackTime(currentTime)}</span>
          <div className={cn('progress-track')}>
            <div
              className={cn('progress-fill')}
              style={{ width: `${progressPct}%`, background: accentColor }}
            />
          </div>
          <span className={cn('time-total')}>
            {item.hasVoice && durationSec > 0 ? formatPlaybackTime(durationSec) : '—'}
          </span>
        </div>

        <div className={cn('card-footer')}>
          <div className={cn('card-date')}>
            <Calendar />
            <span>{item.date}</span>
          </div>
          <span
            className={cn('position-tag')}
            style={{ background: tagStyle.bg, color: tagStyle.color }}
          >
            {item.position}
          </span>
        </div>
      </article>

      <SpeakerModal
        show={openModal}
        handleClose={() => setOpenModal(false)}
        type="edit"
        onRefresh={onRefresh}
        item={item}
      />
    </>
  );
};

export default SpeakerCard;
